import { useState } from 'react'
import { API_CONFIG } from '../config/settings'

export interface BookingRequest {
  room_id: number
  check_in: string
  check_out: string
  guests: number
  full_name: string
  phone: string
  email: string
  user_id?: number
  payment_method: string
  coupon_code?: string
  total_amount: number
  /** Amount to charge via Cashfree now (full total + fee, or partial 20% + service charge). */
  payable_amount?: number
  service_charge?: number
  special_requests?: string
}

export interface BookingResponse {
  booking_number: number
  room_id: number
  check_in: string
  check_out: string
  guests: number
  customer_name: string
  customer_phone: string
  customer_email: string
  payment_method: string
  grand_total: number
  payment_status: number
  payment_session_id?: string | null
  cf_order_id?: string | null
  amount_due_now?: number | null
  balance_due_at_checkin?: number | null
  partial_service_fee?: number | null
  bridge_url?: string | null
  checkout_mode?: 'partner_redirect' | 'cashfree_modal' | 'offline' | null
  created_at: string
}

export interface ConfirmPaymentRequest {
  payment_id: string
  payment_status?: 'confirmed' | 'success' | 'paid' | 'completed' | 'failed' | 'cancelled' | 'pending'
  payment_method?: string
  notes?: string
}

export interface OrderResponse {
  booking_number: number
  payment_id: string | null
  payment_status: string
  amount: number
  currency: string
  payment_method: string | null
  notes: string | null
  updated_at: string
}

export const useBooking = () => {
  const [isBooking, setIsBooking] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingSuccess, setBookingSuccess] = useState<BookingResponse | null>(null)

  const createBooking = async (bookingData: BookingRequest): Promise<BookingResponse> => {
    try {
      setIsBooking(true)
      setBookingError(null)
      setBookingSuccess(null)

      const response = await fetch(`${API_CONFIG.BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        
        // Handle Pydantic validation errors
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map((err: any) => {
            const field = err.loc[err.loc.length - 1] // Get the field name
            return `${field}: ${err.msg}`
          }).join(', ')
          throw new Error(`Validation Error: ${validationErrors}`)
        }
        
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }

      const raw = await response.json()
      const booking: BookingResponse = {
        ...raw,
        booking_number: Number(raw.booking_number ?? raw.bookingNumber),
      }
      if (!Number.isFinite(booking.booking_number)) {
        throw new Error('Invalid booking response: missing booking number')
      }
      setBookingSuccess(booking)
      return booking

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Booking failed'
      setBookingError(errorMessage)
      throw error
    } finally {
      setIsBooking(false)
    }
  }

  const getBooking = async (bookingId: string): Promise<BookingResponse> => {
    try {
      setIsBooking(true)
      setBookingError(null)

      const response = await fetch(`${API_CONFIG.BASE_URL}/bookings/${bookingId}`)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }

      const booking = await response.json()
      return booking

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch booking'
      setBookingError(errorMessage)
      throw error
    } finally {
      setIsBooking(false)
    }
  }

  const confirmPayment = async (
    bookingNumber: number,
    payload: ConfirmPaymentRequest
  ): Promise<OrderResponse> => {
    try {
      setIsBooking(true)
      setBookingError(null)

      const response = await fetch(`${API_CONFIG.BASE_URL}/orders/${bookingNumber}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to confirm payment'
      setBookingError(errorMessage)
      throw error
    } finally {
      setIsBooking(false)
    }
  }

  const verifyPayment = async (bookingNumber: number): Promise<OrderResponse> => {
    try {
      setIsBooking(true)
      setBookingError(null)

      const response = await fetch(`${API_CONFIG.BASE_URL}/orders/${bookingNumber}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify payment'
      setBookingError(errorMessage)
      throw error
    } finally {
      setIsBooking(false)
    }
  }

  const verifyPartnerPayment = async (orderNo: string, cfOrderId: string) => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/bookings/payment/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_no: orderNo, cf_order_id: cfOrderId }),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(json.detail || json.error || 'Payment verification failed')
    }
    return json
  }

  const resetBooking = () => {
    setBookingError(null)
    setBookingSuccess(null)
  }

  const patchBookingSuccess = (patch: Partial<BookingResponse>) => {
    setBookingSuccess((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  return {
    isBooking,
    bookingError,
    bookingSuccess,
    createBooking,
    getBooking,
    confirmPayment,
    verifyPayment,
    verifyPartnerPayment,
    resetBooking,
    patchBookingSuccess,
  }
}
